import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Renderer2 as Renderer
} from '@angular/core';
import {
  BatteryModelEnum,
  RDSAzzurroEventsSortInput,
  RDSAzzurroEvents,
  RDSAzzurroEventsEnum,
  RDSAzzurroEventsFilter,
  RDSEventModel,
  RDSPaginationInput,
  RDSSortDirectionEnum,
  SystemsService,
  RDSAzzurroEventsSortEnum,
} from 'ngx-core-services';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { ColorTypeEnum, ColumnConfig, ColumnTypeEnum } from 'ngx-zcs-components';
import { BehaviorSubject, combineLatest, from, Subject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { FormBuilder, FormGroup } from '@angular/forms';

import { EventRegistryService } from './event-registry.service';
import { AccessSupportService } from '../../communication/access-support.service';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { getChipColor } from '../device-registry/device-registry.component';

const getLabels = () => ({
  actions: 'Azioni',
  eventFilter: 'Filtro Eventi',
  eventId: 'ID Evento',
  eventIdPlaceholder: 'ID evento',
  eventName: 'Nome evento',
  eventRegistryTitle: 'Registro eventi',
  applyFilters: 'Applica filtri',
  batteryModel: 'Battery Model',
  dspmPlaceholder: 'DSPM',
  dspsPlaceholder: 'DSPS',
  firmwareFilters: 'Filtri firmware',
  armPlaceholder: 'ARM',
  createdAt: 'Data Creazione',
  deviceFilter: 'Filtro dispositivo',
  deviceId: 'ID Dispositivo',
  generationPlaceholder: 'Generazione Inverter',
  itemsPerPageLabel: 'Elementi per pagina',
  noData: 'Nessun dato disponibile',
  noResults: 'Nessun risultato trovato',
  of: 'di',
  resetFilters: 'Resetta filtri',
  search: 'Cerca',
  selectColumns: 'Seleziona colonne',
  status: 'Stato',
  statusPlaceholder: 'Seleziona Stato',
  updatedAt: 'Last Update',
});

@CreateCustomBlockSizeType({
  'breakpoint-500': 500,
})
@Component({
  selector: 'lib-event-registry',
  templateUrl: './event-registry.component.html',
  styleUrls: ['./event-registry.component.scss'],
})
export class EventRegistryComponent implements ICustomBlock, AfterViewInit {
  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.updateColumnLabels();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() deviceId: string | undefined | null;

  eventsOptions = Object.entries(RDSEventModel).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' '),
  }));

  batteryModelOptions = Object.entries(BatteryModelEnum).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' '),
  }));

  statusOptions = Object.entries(RDSAzzurroEventsEnum).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' '),
  }));

  defaultPaginationConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 25, 50],
    length: 100,
  };

  paginationConfig: BehaviorSubject<typeof this.defaultPaginationConfig> = new BehaviorSubject(this.defaultPaginationConfig);

  columns: ColumnConfig[] = [];

  sort: Subject<RDSAzzurroEventsSortInput> = new Subject();
  pagination: BehaviorSubject<RDSPaginationInput> = new BehaviorSubject({ pageIndex: 0, pageSize: 10 });
  filter: Subject<RDSAzzurroEventsFilter> = new Subject();
  data$ = combineLatest([
    this.pagination,
    this.sort.pipe(startWith(undefined)),
    this.filter.pipe(startWith({})),
  ]).pipe(
    switchMap(([pagination, sort, filter]) =>
      from(this.eventRegistryService.getEvents(pagination, filter, sort)).pipe(
        tap((data) => this.paginationConfig.next({ ...this.paginationConfig.value, length: data?.totalCount || 0 })),
        startWith(null)
      )
    ),
    map((data) => data?.azzurroEvents ?? null),
  );

  filterForm!: FormGroup;

  constructor(
    private readonly accessSupportService: AccessSupportService,
    private readonly cdr: ChangeDetectorRef,
    private readonly eventRegistryService: EventRegistryService,
    private readonly fb: FormBuilder,
    readonly el: ElementRef,
    readonly elementRef: ElementRef,
    readonly renderer: Renderer,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly systemsService: SystemsService,
  ) {
    this.onSubmit = this.onSubmit.bind(this);
    this.resetFilter = this.resetFilter.bind(this);
  }

  ngAfterViewInit(): void {
    this.updateColumnLabels();
    this.filterForm = this.fb.group({
      azzurroEvent: [null],
      deviceId: [''],
      arm: [''],
      dspm: [''],
      dsps: [''],
      batteryModel: [null],
      status: [null]
    });

    if (this.deviceId) {
      this.filterForm.patchValue({ deviceId: this.deviceId });
      this.filter.next({ deviceId: this.deviceId });
    } else {
      const savedFilters = localStorage.getItem('eventRegistryFilters');
      if (savedFilters) {
        this.filterForm.patchValue(JSON.parse(savedFilters));
        this.filter.next(JSON.parse(savedFilters));
      }
    }

    this.cdr.detectChanges();
  }

  onPageChange(event: PageEvent) {
    this.pagination.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onSortChange(event: Sort) {
    this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize });

    const sortColumn = event.active as keyof typeof RDSAzzurroEventsSortEnum;
    const direction = event.direction === 'asc' ? RDSSortDirectionEnum.ASC : RDSSortDirectionEnum.DESC;

    if (sortColumn in RDSAzzurroEventsSortEnum) {
      this.sort.next({
        column: RDSAzzurroEventsSortEnum[sortColumn],
        sortDirection: direction,
      });
    } else {
      this.sort.next(undefined);
    }
  }

  updateColumnLabels(): void {
    this.columns = [
      {
        columnDef: 'eventName',
        header: this.labels.eventName,
        type: ColumnTypeEnum.Text,
        cell: (row: RDSAzzurroEvents) => row.azzurroEvent,
        sortable: false,
      },
      {
        columnDef: 'deviceId',
        header: this.labels.deviceId,
        type: ColumnTypeEnum.Text,
        cell: (row: RDSAzzurroEvents) => row.deviceId,
        sortable: false,
      },
      {
        columnDef: 'status',
        header: this.labels.status,
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAzzurroEvents) => row.status === RDSAzzurroEventsEnum.OPEN ? 'Aperto' : 'Chiuso',
        chipColor: (row: RDSAzzurroEvents) => row.status === RDSAzzurroEventsEnum.OPEN ? ColorTypeEnum.Yellow : ColorTypeEnum.Primary,
        sortable: false,
      },
      {
        columnDef: 'batteryModel',
        type: ColumnTypeEnum.Text,
        header: this.labels.batteryModel,
        cell: (row: RDSAzzurroEvents) => row.batteryModel,
        sortable: false,
      },
      {
        columnDef: 'arm',
        header: 'ARM',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAzzurroEvents) => row.arm,
        cellHidden: (row: RDSAzzurroEvents) => !row.arm,
        chipColor$: (row: RDSAzzurroEvents) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'dspm',
        header: 'DSPM',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAzzurroEvents) => row.dspm,
        cellHidden: (row: RDSAzzurroEvents) => !row.dspm,
        chipColor$: (row: RDSAzzurroEvents) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'dsps',
        header: 'DSPS',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAzzurroEvents) => row.dsps,
        cellHidden: (row: RDSAzzurroEvents) => !row.dsps,
        chipColor$: (row: RDSAzzurroEvents) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'createdAt',
        header: this.labels.createdAt,
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy HH:mm',
        cell: (row: RDSAzzurroEvents) => row.createdAt,
        sortable: true,
      },
      {
        columnDef: 'updatedAt',
        header: this.labels.updatedAt,
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy HH:mm',
        cell: (row: RDSAzzurroEvents) => row.updatedAt,
        sortable: true,
      },
    ];
  }

  onSubmit(): void {
    if (!this.filterForm.valid) {
      return;
    }

    localStorage.setItem('eventRegistryFilters', JSON.stringify(this.filterForm.value));
    this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize });
    const filters = this.filterForm.value;
    this.filter.next({
      deviceId: filters.deviceId,
      batteryModel: filters.batteryModel,
      status: filters.status,
      azzurroEvent: filters.azzurroEvent,
      arm: filters.arm,
      dspm: filters.dspm,
      dsps: filters.dsps,
    });
  }

  resetFilter(): void {
    this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize });
    this.filterForm.reset();
    localStorage.removeItem('eventRegistryFilters');
    this.filter.next({});
  }

  onFilterChange(filter: RDSAzzurroEventsFilter) {
    this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize });
    this.filter.next(filter);
  }
}
